// src/Pages/Auth/variants/industries.js
//
// Industry content for campaign traffic (ads, blogs, social posts).
//
// The industry comes from utm_content: everything before the first '.'.
//   utm_content=restaurant      -> restaurant
//   utm_content=restaurant.v2   -> restaurant (v2 is a creative variant, funnel-only)
//
// Each industry has a MODE that decides what the page leads with:
//   social   posts first, review replies mentioned once
//   both     reviews and posts together
//   reviews  review replies first, social posts mentioned once
//
// TO ADD AN INDUSTRY: add an entry to INDUSTRIES. Key = the utm_content value.
//
// OPTIONAL STATS (shown above the flow card, max 3):
//   stats:     [{ value: '64%', label: 'leave negative reviews unanswered' }, ...]
//   statsNote: 'From 180 Greater Boston salons Revfer reviewed, Oct 2026'
// Every stat needs a statsNote saying where it came from. Our own outreach data:
// sample size, region, month. Published stats: the source.
//
// COUNTRY OVERRIDES (optional):
//   byCountry: { us: { stats: [...], statsNote: '...' }, in: { flow: [...] } }
// Any field can be overridden per country. ctx.country comes from ?cc= or the
// browser timezone, and is null when unknown — so the BASE entry must read
// correctly anywhere. Region-specific stats/examples go in byCountry only.
// Lowercase, underscores, no dots.

import {
  RateReview as ReviewIcon,
  WbSunny as WeatherIcon,
  Celebration as EventIcon,
  QuestionAnswer as QuestionIcon,
  Campaign as AnnounceIcon,
  MenuBook as MenuIcon,
} from '@mui/icons-material';

// What each outcome is. Drives icon colour on the flow card.
export const KIND = {
  social: { color: '#0277BD', bg: 'rgba(2,136,209,0.08)', border: 'rgba(2,136,209,0.2)' },
  review: { color: '#2E7D32', bg: 'rgba(46,125,50,0.08)', border: 'rgba(46,125,50,0.2)' },
  faq:    { color: '#5E35B1', bg: 'rgba(94,53,177,0.08)', border: 'rgba(94,53,177,0.2)' },
};

// Per-mode pieces that every industry in that mode shares.
const MODE = {
  social: {
    accent: 'social',
    addBusiness: 'Name and city. Revfer writes your first posts from it.',
    connect: {
      label: 'Connect your Facebook or Instagram page',
      detail: 'So approved posts can publish. This is the only step that asks for permissions. LinkedIn and more channels are coming soon.',
    },
    secondary: 'Revfer also drafts replies to your Google reviews, for you to approve the same way. Same account.',
  },
  both: {
    accent: 'social',
    addBusiness: 'Name and address. We find your Google listing and write your first posts.',
    connect: {
      label: 'Connect Google and your social pages',
      detail: 'So approved replies and posts can publish. These are the only steps that ask for permissions.',
    },
    secondary: null,
  },
  reviews: {
    accent: 'review',
    addBusiness: 'Name and address. We find your Google listing.',
    connect: {
      label: 'Connect Google',
      detail: 'So approved replies can post to your Google profile. This is the only step that asks for permissions.',
    },
    secondary: 'Revfer also turns your good reviews into Facebook and Instagram posts. Same account.',
  },
};

export const INDUSTRIES = {
  tech_startup: {
    mode: 'social',
    chip: 'For founders who know they should be posting',
    headline: 'You build. Revfer posts.',
    headlineAccent: 'You just approve.',
    subhead:
      'Revfer writes your Facebook and Instagram posts from what is actually happening at ' +
      'your company: a new feature, the question customers keep asking, an event coming up. ' +
      'You read each one on your phone and tap approve.',
    flowTitle: 'A WEEK OF POSTS, WITHOUT WRITING ONE',
    flow: [
      { Icon: AnnounceIcon, kind: 'social', signal: 'You note a new feature in one line', signalAt: 'Monday',
        outcome: 'Facebook and Instagram post written from it', outcomeAt: 'Draft', mobileOutcome: 'Post written from it' },
      { Icon: QuestionIcon, kind: 'social', signal: 'Third customer asks the same question', signalAt: 'this month',
        outcome: 'Instagram post answering it, drafted', outcomeAt: 'Draft', mobileOutcome: 'Post answering it, drafted' },
      { Icon: EventIcon, kind: 'social', signal: 'A startup event in your city next week', signalAt: '',
        outcome: 'Post written and scheduled', outcomeAt: 'Ready', mobileOutcome: 'Post written and scheduled' },
    ],
    flowFooter: 'Nothing goes out until you approve it.',
    // Google review replies aren't the pitch for founders — drop the mode's secondary line.
    secondary: null,
    stats: [
      { value: '62%', label: 'of Instagram users research brands there' },
      { value: '78%', label: 'of consumers say brand posts influence them' },
      { value: '1 in 3', label: 'come across new brands on social every day' },
    ],
    statsNote: 'Sources: GWI via DataReportal, Digital 2025; Clutch consumer surveys, 2025 and 2026.',
  },

  restaurant: {
    mode: 'both',
    addBusiness: 'Name and address. We find your Google listing. Add your menu and Revfer plans posts around your dishes.',
    chip: 'For restaurants, cafes and bakeries',
    headline: 'Reviews answered. Specials posted.',
    headlineAccent: 'You just approve.',
    subhead:
      'Revfer drafts replies to your Google reviews and writes your Facebook and Instagram posts ' +
      'from what is happening around you: the weather, local events, your best reviews. ' +
      'You approve from your phone.',
    flowTitle: 'A WEEK AT YOUR RESTAURANT',
    flow: [
      { Icon: ReviewIcon, kind: 'review', signal: 'A 3-star review lands', signalAt: '9:02pm',
        outcome: 'Reply drafted, posts to Google on approval', outcomeAt: '9:06pm', mobileOutcome: 'Reply drafted by 9:06pm' },
      { Icon: WeatherIcon, kind: 'social', signal: 'Rainy weekend ahead', signalAt: 'Thursday',
        outcome: 'Comfort-food post scheduled to Instagram', outcomeAt: 'Fri 11am', mobileOutcome: 'Post scheduled to Instagram' },
      { Icon: MenuIcon, kind: 'social', signal: 'You upload your menu once', signalAt: '',
        outcome: 'Revfer learns your dishes and plans posts around them', outcomeAt: 'Ongoing', mobileOutcome: 'Posts planned around your dishes' },
    ],
    flowFooter: 'Your whole job is tapping approve.',
  },

  salon: {
    mode: 'both',
    chip: 'For salons, barbers, nail and beauty studios',
    headline: 'Reviews answered. Your work, posted.',
    headlineAccent: 'You just approve.',
    subhead:
      'Revfer drafts replies to your Google reviews and turns your best ones into ' +
      'Instagram and Facebook posts. You read them on your phone between clients and tap approve.',
    flowTitle: 'A WEEK AT YOUR SALON',
    flow: [
      { Icon: ReviewIcon, kind: 'review', signal: 'A 2-star review lands', signalAt: '9:02pm',
        outcome: 'Careful reply drafted, waits for you', outcomeAt: 'Draft', mobileOutcome: 'Careful reply drafted, waits for you' },
      { Icon: ReviewIcon, kind: 'social', signal: 'A client raves about their new colour', signalAt: 'Tuesday',
        outcome: 'Turned into an Instagram post', outcomeAt: 'Draft', mobileOutcome: 'Turned into an Instagram post' },
      { Icon: EventIcon, kind: 'social', signal: 'Wedding season coming up', signalAt: '',
        outcome: 'Bridal booking post written and scheduled', outcomeAt: 'Ready', mobileOutcome: 'Bridal post scheduled' },
    ],
    flowFooter: 'Nothing posts until you approve it.',
    byCountry: {
      us: {
        stats: [
          { value: '4 in 10', label: "haven't replied to any recent review" },
          { value: 'Over half', label: 'reply to fewer than 1 in 4 reviews' },
          { value: 'Nearly 1 in 3', label: 'have a recent negative review left unanswered' },
        ],
        statsNote:
          "From Revfer's look at the 10 most recent Google reviews of about 1,000 " +
          'established Greater Boston salons, barbers and spas, 2026.',
      },
    },
  },

  professional_services: {
    mode: 'reviews',
    chip: 'For accountants, consultants, agencies and other professional firms',
    headline: 'Every Google review answered.',
    headlineAccent: 'Without you writing one.',
    subhead:
      'Clients read your reviews, and your replies, before they call. Revfer drafts a reply ' +
      'to every new Google review in your voice. You read it on your phone, tap approve, and it posts.',
    flowTitle: 'EVERY REVIEW, FROM NOW ON',
    flow: [
      { Icon: ReviewIcon, kind: 'review', signal: 'A 5-star review lands', signalAt: '9:02pm',
        outcome: 'Reply drafted in your voice', outcomeAt: '9:06pm', mobileOutcome: 'Reply drafted by 9:06pm' },
      { Icon: ReviewIcon, kind: 'review', signal: 'A critical 2-star review', signalAt: 'Saturday',
        outcome: 'Careful reply drafted, waits for you', outcomeAt: 'Draft', mobileOutcome: 'Careful reply drafted, waits for you' },
      { Icon: QuestionIcon, kind: 'faq', signal: 'Fourth client asks about fees', signalAt: 'this month',
        outcome: 'FAQ published, chatbot answers it 24/7', outcomeAt: 'Live', mobileOutcome: 'FAQ live, chatbot answers 24/7' },
    ],
    flowFooter: 'Nothing posts until you approve it.',
  },

  // One-person service businesses: tutors, tailors, agents, photographers,
  // repair. "Other Professional Services" in our data — the largest group of
  // ChatGPT-ad signups (mostly India), and their pitch is posts, not Google
  // reviews, so they get their own entry rather than professional_services.
  local_services: {
    mode: 'social',
    chip: 'For tutors, tailors, agents, photographers and local services',
    headline: 'Your services, posted every week.',
    headlineAccent: 'You just approve.',
    subhead:
      'Tell Revfer what you offer once. It writes Facebook and Instagram posts about your ' +
      'services, your offers and the festivals coming up. You approve each one from your phone.',
    addBusiness: 'Name, city and two or three services you offer. Revfer writes your first posts from it.',
    flowTitle: 'A WEEK OF POSTS, WITHOUT WRITING ONE',
    flow: [
      { Icon: AnnounceIcon, kind: 'social', signal: 'You type one line: "New weekend batch starts Saturday"', signalAt: 'Monday',
        outcome: 'Facebook and Instagram post written from it', outcomeAt: 'Draft', mobileOutcome: 'Post written from it' },
      { Icon: EventIcon, kind: 'social', signal: 'A festival is coming up', signalAt: 'next week',
        outcome: 'Festival offer post written and scheduled', outcomeAt: 'Ready', mobileOutcome: 'Festival post scheduled' },
      { Icon: QuestionIcon, kind: 'social', signal: 'A busy week, nothing posted', signalAt: '',
        outcome: 'Post about one of your services, written anyway', outcomeAt: 'Draft', mobileOutcome: 'Post about a service, written anyway' },
    ],
    flowFooter: 'Nothing goes out until you approve it.',
    secondary: null,
  },

  // Shops, boutiques, home sellers. "Retail" — second-largest ad signup group.
  retail: {
    mode: 'social',
    chip: 'For shops, boutiques and home businesses',
    headline: 'New stock in. Post written.',
    headlineAccent: 'You just approve.',
    subhead:
      'Revfer writes Facebook and Instagram posts about what you sell: new arrivals, ' +
      'festival offers, your best sellers. You read each one on your phone and tap approve.',
    addBusiness: 'Name, city and what you sell. Revfer writes your first posts from it.',
    flowTitle: 'A WEEK AT YOUR SHOP',
    flow: [
      { Icon: AnnounceIcon, kind: 'social', signal: 'You type one line: "New arrivals are in"', signalAt: 'Tuesday',
        outcome: 'Instagram and Facebook post written from it', outcomeAt: 'Draft', mobileOutcome: 'Post written from it' },
      { Icon: EventIcon, kind: 'social', signal: 'A festival sale coming up', signalAt: 'next week',
        outcome: 'Offer post written and scheduled', outcomeAt: 'Ready', mobileOutcome: 'Offer post scheduled' },
      { Icon: QuestionIcon, kind: 'social', signal: 'A slow week, nothing posted', signalAt: '',
        outcome: 'Post about one of your best sellers, written anyway', outcomeAt: 'Draft', mobileOutcome: 'Best-seller post, written anyway' },
    ],
    flowFooter: 'Nothing goes out until you approve it.',
  },
};

// Paid ads with no industry: the ad pitches social posting, so continue that.
const GENERIC_ADS = {
  mode: 'social',
  chip: 'Social posts and review replies, written for you',
  headline: 'Your posts, written.',
  headlineAccent: 'You just approve.',
  subhead:
    'Revfer writes your Facebook and Instagram posts from what is actually ' +
    'happening at your business. You read each one on your phone and tap approve.',
  flowTitle: 'A WEEK OF POSTS, WITHOUT WRITING ONE',
  flow: [
    { Icon: ReviewIcon, kind: 'social', signal: 'A 5-star review comes in', signalAt: 'Tuesday',
      outcome: 'Turned into an Instagram post', outcomeAt: 'Draft', mobileOutcome: 'Turned into an Instagram post' },
    { Icon: WeatherIcon, kind: 'social', signal: 'Hot week ahead', signalAt: 'Monday',
      outcome: 'Post written for it, scheduled to Facebook', outcomeAt: 'Wed 10am', mobileOutcome: 'Post scheduled to Facebook' },
    { Icon: EventIcon, kind: 'social', signal: 'A local festival next week', signalAt: '',
      outcome: 'Festival post written and scheduled', outcomeAt: 'Ready', mobileOutcome: 'Festival post written and scheduled' },
  ],
  flowFooter: 'Nothing goes out until you approve it.',
};

/** Industry key from utm_content, or null if missing / unknown. */
export function industryKey(ctx) {
  const raw = ctx?.utm?.utm_content;
  if (typeof raw !== 'string') return null;
  const key = raw.toLowerCase().split('.')[0].trim();
  return key && Object.prototype.hasOwnProperty.call(INDUSTRIES, key) ? key : null;
}

/**
 * Content for this visitor, or null if the industry variant shouldn't render.
 *   campaign + known industry      -> that industry
 *   campaign + paid, no industry   -> generic social (matches the ad)
 *   anything else                  -> null (default narrative)
 */
export function resolveIndustry(ctx) {
  if (ctx?.source !== 'campaign') return null;
  const key = industryKey(ctx);
  const base = key ? INDUSTRIES[key] : (ctx.utm?.utm_medium === 'cpc' ? GENERIC_ADS : null);
  if (!base) return null;
  const local = (ctx.country && base.byCountry?.[ctx.country]) || {};
  const merged = { ...base, ...local };
  const mode = MODE[merged.mode] || MODE.social;
  // Mode supplies defaults; an industry or country entry may override them.
  return { ...mode, ...merged, key: key || 'generic_ads', country: ctx.country || null };
}